/**
 * 企微朋友圈 - 接口定义
 * 分页参数用 page_size（渠道账号接口用的是 per_page，勿混）
 */
import { apiGet, apiPost, apiPut, apiRequest } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import type {
  QywxMomentImage,
  QywxMomentPlan,
  QywxMomentPlanPayload,
  QywxMomentTask,
  QywxMomentTenant,
  QywxMomentTenantKeyResult,
  QywxMomentTenantPayload,
} from '@/types/qywxMoment';

export const qywxMomentApi = {
  /** 租户配置；未配置时返回 configured=false，不是报错 */
  tenant: () => apiGet<QywxMomentTenant>('/qywx/moment/tenant'),

  /** 首次录入 / 重新注册，一个应用只有一条配置，重复调用是覆盖 */
  tenantRegister: (data: QywxMomentTenantPayload) =>
    apiPost<QywxMomentTenantKeyResult>('/qywx/moment/tenant-register', data),

  /** 修改配置：secret 传空串表示不改密钥；allowed_sender_userids 传 [] 是清空白名单 */
  tenantUpdate: (data: QywxMomentTenantPayload) =>
    apiPut<QywxMomentTenant>('/qywx/moment/tenant-update', data),

  /** 重置 api_key，返回的新 key 仅此一次可见 */
  tenantResetKey: () => apiPost<QywxMomentTenantKeyResult>('/qywx/moment/tenant-reset-key', {}),

  /** 上传图片素材，拿到的 media_id 与 url 需成对存下并在建计划时一起回传 */
  uploadMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiPost<QywxMomentImage>('/qywx/moment/upload-media', form);
  },

  /** 计划列表，支持 name / plan_state / account_id 筛选（status 是兼容老调用的字段，不再使用） */
  plans: (params: Record<string, unknown> = {}) =>
    apiGet('/qywx/moment/plans', { page: 1, page_size: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<QywxMomentPlan>(d),
    ),

  /** 计划详情，结构与列表项一致，用于编辑回填 */
  planShow: (id: number) => apiGet<QywxMomentPlan>('/qywx/moment/plans/show', { id }),

  planCreate: (data: QywxMomentPlanPayload) => apiPost('/qywx/moment/plans/create', data),

  /** 编辑是「传即改」：content 传空串、images 传 [] 都是真清空，整表单回传即可 */
  planUpdate: (data: QywxMomentPlanPayload & { id: number }) =>
    apiPut('/qywx/moment/plans/update', data),

  /**
   * 变更计划状态，plan_state 只接受 2 恢复为进行中 / 3 暂停 / 4 结束。
   * 已结束的计划再调返回 422，改完立即生效
   */
  planToggle: (id: number, plan_state: number) =>
    apiPut('/qywx/moment/plans/toggle', { id, plan_state }),

  /** 软删，任何状态都可删 */
  planDelete: (id: number) =>
    apiRequest('/qywx/moment/plans/delete', { method: 'DELETE', data: { id } }),

  /** 发送记录，按 plan_id 查某个计划的下发情况 */
  tasks: (params: Record<string, unknown> = {}) =>
    apiGet('/qywx/moment/tasks', { page: 1, page_size: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<QywxMomentTask>(d),
    ),
};
